import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import { Event } from "@/pages/events/types";
import { CANCEL_EVENT_MUTATION } from "../../../queries";
import { useMutation } from "@apollo/client";
import { showSnackbar } from "@/lib/snackbarUtils";
import { useParams } from "react-router-dom";
import useAmplitude from "@/hooks/useAmplitude";
import { EVENT_ANALYTICS_EVENTS, getEventAnalyticsData } from "@/pages/events/_utils/amplitude";

export type CancelEvent = Pick<Event, "id" | "registrationCount" | "hasActiveOrders"> & { slug?: string };

interface CancelEventDialogProps {
  event: CancelEvent;
  open: boolean;
  setOpen: (value: boolean) => void;
  refetch?: () => void;
}

export default function CancelEventModal({ event, open, setOpen, refetch }: CancelEventDialogProps) {
  const { eventSlug: eventSlugParam = "" } = useParams();
  const eventSlug = event.slug || eventSlugParam;
  const { trackEvent } = useAmplitude();
  const [cancelEvent] = useMutation(CANCEL_EVENT_MUTATION, {
    onCompleted: (data) => {
      if (data.cancelEvent.success) {
        showSnackbar("Event cancelled successfully", { close: true });
        refetch?.(); // Only refetch when cancellation is successful
        setOpen(false);
        trackEvent(
          EVENT_ANALYTICS_EVENTS.canceled,
          getEventAnalyticsData({
            source: "cancel_event",
            eventId: event.id,
            eventSlug,
            action: "cancel_event",
            count: event.registrationCount
          })
        );
      } else {
        showSnackbar(`Error: ${data.cancelEvent.message}`);
      }
    },
    onError: (error) => {
      showSnackbar(`Error: ${error.message}`);
    }
  });

  const confirmationMessage = () => {
    const hasRegistrations = (event.registrationCount ?? 0) > 0;
    const hasActiveOrders = !!event?.hasActiveOrders;

    if (hasRegistrations && !hasActiveOrders) {
      return "Are you sure you want to cancel this event? All registrants will be notified. This cannot be undone.";
    } else if (hasRegistrations && hasActiveOrders) {
      return "Are you sure you want to cancel this event? All registrants will be notified and their payments refunded. This cannot be undone.";
    } else {
      return "Are you sure you want to cancel this event? This cannot be undone.";
    }
  };

  async function handleCancel() {
    try {
      await cancelEvent({ variables: { id: event.id } });
    } catch (e) {
      showSnackbar(`Error: ${e}`);
    }
  }

  function handleClose() {
    setOpen(false);
  }

  return (
    <Dialog
      open={open}
      onClosed={() => setOpen(false)}
      className="max-w-[423px] p-6 pr-2"
      headline="Cancel Event"
      hasFormElements={false}
      actions={
        <>
          <Button variant="text" onClick={handleClose}>
            Go Back
          </Button>
          <Button variant="filled" onClick={handleCancel} testId="cancel-event-button">
            Cancel Event
          </Button>
        </>
      }
    >
      <p>{confirmationMessage()}</p>
    </Dialog>
  );
}
